import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Avatar,
  Fab,
  Tabs,
  Tab,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText as MuiListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Storage as StorageIcon,
  Schema as SchemaIcon,
  DataObject as DataIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Archive as ArchiveIcon,
  PlayArrow as ActiveIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Publish as PublishIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { schemasAPI, websitesAPI, dataAPI } from '../../services/api';
import SchemaRecordsDialog from './SchemaRecordsDialog';
import ApiEndpointInfo from './ApiEndpointInfo';
import DataRecordDialog from './DataRecordDialog';
import WebsiteDataDialog from './WebsiteDataDialog';

// Dropdown Options Editor Component
const DropdownOptionsEditor = ({ options = [], onChange, disabled = false }) => {
  const handleAddOption = () => {
    const newOptions = [...options, { value: '', label: '', isDefault: false }];
    onChange(newOptions);
  };

  const handleUpdateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };

    // If setting as default, unset other defaults
    if (field === 'isDefault' && value) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.isDefault = false;
      });
    }

    onChange(newOptions);
  };

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  return (
    <Box>
      {options.length === 0 ? (
        <Box sx={{
          border: '1px dashed #ccc',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          bgcolor: 'grey.50'
        }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            No dropdown options added yet
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            disabled={disabled}
          >
            Add First Option
          </Button>
        </Box>
      ) : (
        <Box>
          {options.map((option, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 1, p: 1 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Value"
                    value={option.value || ''}
                    onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="category-1"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Label"
                    value={option.label || ''}
                    onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="Category 1"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={option.isDefault || false}
                        onChange={(e) => handleUpdateOption(index, 'isDefault', e.target.checked)}
                        disabled={disabled}
                        size="small"
                      />
                    }
                    label="Default"
                    labelPlacement="top"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveOption(index)}
                    disabled={disabled}
                  >
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            Add Option
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Object Array Schema Editor Component
const ObjectArraySchemaEditor = ({ arraySchema = [], onChange, disabled = false }) => {
  const handleAddProperty = () => {
    const newSchema = [...arraySchema, {
      name: '',
      label: '',
      type: 'string',
      required: false,
      description: '',
      options: []
    }];
    onChange(newSchema);
  };

  const handleUpdateProperty = (index, field, value) => {
    const newSchema = [...arraySchema];
    newSchema[index] = { ...newSchema[index], [field]: value };
    onChange(newSchema);
  };

  const handleRemoveProperty = (index) => {
    const newSchema = arraySchema.filter((_, i) => i !== index);
    onChange(newSchema);
  };

  const handleUpdatePropertyOptions = (index, options) => {
    const newSchema = [...arraySchema];
    newSchema[index] = { ...newSchema[index], options };
    onChange(newSchema);
  };

  const propertyTypes = ['string', 'number', 'boolean', 'date', 'email', 'url', 'text', 'dropdown'];

  return (
    <Box>
      {arraySchema.length === 0 ? (
        <Box sx={{
          border: '1px dashed #ccc',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          bgcolor: 'grey.50'
        }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            No object properties defined yet
          </Typography>
          <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: 'block' }}>
            Define the structure of objects that will be stored in this array
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddProperty}
            disabled={disabled}
          >
            Add First Property
          </Button>
        </Box>
      ) : (
        <Box>
          {arraySchema.map((property, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Property {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveProperty(index)}
                  disabled={disabled}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Property Name"
                    value={property.name || ''}
                    onChange={(e) => handleUpdateProperty(index, 'name', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="size"
                    helperText="Used as the key in JSON objects"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Property Label"
                    value={property.label || ''}
                    onChange={(e) => handleUpdateProperty(index, 'label', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="Size"
                    helperText="Human-readable label"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={property.type || 'string'}
                      onChange={(e) => handleUpdateProperty(index, 'type', e.target.value)}
                      label="Type"
                      disabled={disabled}
                    >
                      {propertyTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description (Optional)"
                    value={property.description || ''}
                    onChange={(e) => handleUpdateProperty(index, 'description', e.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    placeholder="Description of this property"
                  />
                </Grid>

                {/* Dropdown Options for property - only show for dropdown type */}
                {property.type === 'dropdown' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" gutterBottom>
                      Dropdown Options for {property.label || 'Property'}
                    </Typography>
                    <DropdownOptionsEditor
                      options={property.options || []}
                      onChange={(options) => handleUpdatePropertyOptions(index, options)}
                      disabled={disabled}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={property.required || false}
                        onChange={(e) => handleUpdateProperty(index, 'required', e.target.checked)}
                        disabled={disabled}
                        size="small"
                      />
                    }
                    label="Required Property"
                  />
                </Grid>
              </Grid>
            </Card>
          ))}

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddProperty}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            Add Property
          </Button>
        </Box>
      )}
    </Box>
  );
};

const SchemasPage = () => {
  const [schemas, setSchemas] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [dataRecords, setDataRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0 = Data, 1 = Schema
  const [selectedSchemaFilter, setSelectedSchemaFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Schema dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchema, setEditingSchema] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [recordsDialogOpen, setRecordsDialogOpen] = useState(false);
  const [recordsSchema, setRecordsSchema] = useState(null);
  const [apiInfoDialogOpen, setApiInfoDialogOpen] = useState(false);
  const [apiInfoSchema, setApiInfoSchema] = useState(null);

  // Data record dialog states
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecordSchema, setSelectedRecordSchema] = useState(null);

  // Website data dialog states
  const [websiteDataDialogOpen, setWebsiteDataDialogOpen] = useState(false);
  const [websitesWithSchemas, setWebsitesWithSchemas] = useState([]);

  // Publish states
  const [publishingWebsite, setPublishingWebsite] = useState(null);

  // JSON Import states
  const [jsonImportOpen, setJsonImportOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [detectedFields, setDetectedFields] = useState([]);
  const [jsonErrors, setJsonErrors] = useState([]);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      websiteId: '',
      fields: [{ name: '', label: '', type: 'string', required: false, description: '', options: [] }],
      settings: {
        enableVersioning: true,
        enableValidation: true,
        enableIndexing: true,
        maxRecords: 1000
      }
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fields'
  });

  const watchedName = watch('name');
  const watchedSlug = watch('slug');

  useEffect(() => {
    fetchSchemas();
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchDataRecords();
    }
  }, [tabValue, selectedSchemaFilter, page, rowsPerPage]);

  // Auto-generate slug from name
  useEffect(() => {
    if (watchedName && !editingSchema) {
      const slug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9\s-_]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setValue('slug', slug);
    }
  }, [watchedName, editingSchema, setValue]);

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const response = await schemasAPI.getSchemas();
      setSchemas(response.data.schemas);
    } catch (error) {
      console.error('Error fetching schemas:', error);
      toast.error('Failed to load schemas');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await websitesAPI.getWebsites();
      setWebsites(response.data.websites);
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const fetchDataRecords = async () => {
    try {
      setDataLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (selectedSchemaFilter !== 'all') {
        params.schemaId = selectedSchemaFilter;
      }

      const response = await dataAPI.getDataRecords(params);
      setDataRecords(response.data.records);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching data records:', error);
      toast.error('Failed to load data records');
    } finally {
      setDataLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSchemaFilterChange = (event) => {
    setSelectedSchemaFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenRecordDialog = (record = null) => {
    setEditingRecord(record);

    if (record) {
      // Find the schema for this record
      const schema = schemas.find(s => s._id === record.schema._id);
      setSelectedRecordSchema(schema);
    } else {
      // For new records, select the first active schema or the first schema
      const firstActiveSchema = schemas.find(s => s.status === 'active');
      const defaultSchema = firstActiveSchema || schemas[0];
      setSelectedRecordSchema(defaultSchema);
    }

    setRecordDialogOpen(true);
  };

  const handleCloseRecordDialog = () => {
    setRecordDialogOpen(false);
    setEditingRecord(null);
    setSelectedRecordSchema(null);
  };

  const handleRecordSaved = () => {
    fetchDataRecords();
    fetchSchemas(); // Refresh schemas to update record counts
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await dataAPI.deleteRecord(recordId);
        toast.success('Record deleted successfully');
        fetchDataRecords();
        fetchSchemas(); // Refresh schemas to update record counts
      } catch (error) {
        console.error('Error deleting record:', error);
        toast.error('Failed to delete record');
      }
    }
  };

  const handleOpenDialog = (schema = null) => {
    setEditingSchema(schema);
    if (schema) {
      reset({
        name: schema.name,
        slug: schema.slug,
        description: schema.description || '',
        websiteId: schema.website._id,
        fields: schema.fields.map(field => ({
          name: field.name,
          label: field.label,
          type: field.type,
          required: field.required || false,
          description: field.description || '',
          validation: field.validation || {},
          options: field.options || [],
          arraySchema: field.arraySchema || []
        })),
        settings: {
          enableVersioning: schema.settings?.enableVersioning ?? true,
          enableValidation: schema.settings?.enableValidation ?? true,
          enableIndexing: schema.settings?.enableIndexing ?? true,
          maxRecords: schema.settings?.maxRecords || 1000
        }
      });
    } else {
      // For new schemas, automatically include the ID field
      const idField = {
        name: 'id',
        label: 'ID',
        type: 'string',
        required: true,
        description: 'Unique identifier for this record (automatically generated)'
      };

      reset({
        name: '',
        slug: '',
        description: '',
        websiteId: '',
        fields: [idField, { name: '', label: '', type: 'string', required: false, description: '', options: [], arraySchema: [] }],
        settings: {
          enableVersioning: true,
          enableValidation: true,
          enableIndexing: true,
          maxRecords: 1000
        }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchema(null);
    reset();
  };

  const handleSubmitSchema = async (data) => {
    try {
      // Validate unique field names
      const fieldNames = data.fields.map(field => field.name);
      const uniqueNames = [...new Set(fieldNames)];
      if (fieldNames.length !== uniqueNames.length) {
        toast.error('Field names must be unique within a schema');
        return;
      }

      // Remove the ID field from the fields array before sending to backend
      // The backend will add it automatically
      const fieldsWithoutId = data.fields.filter(field => field.name !== 'id');

      const submitData = {
        ...data,
        fields: fieldsWithoutId
      };

      if (editingSchema) {
        await schemasAPI.updateSchema(editingSchema._id, submitData);
        toast.success('Schema updated successfully');
      } else {
        await schemasAPI.createSchema(submitData);
        toast.success('Schema created successfully');
      }

      handleCloseDialog();
      fetchSchemas();
    } catch (error) {
      console.error('Error saving schema:', error);
      toast.error(error.response?.data?.message || 'Failed to save schema');
    }
  };

  const handleDeleteClick = (schema) => {
    setSchemaToDelete(schema);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await schemasAPI.deleteSchema(schemaToDelete._id);
      toast.success('Schema deleted successfully');
      setDeleteDialogOpen(false);
      setSchemaToDelete(null);
      fetchSchemas();
    } catch (error) {
      console.error('Error deleting schema:', error);
      toast.error(error.response?.data?.message || 'Failed to delete schema');
    }
  };

  const handleToggleStatus = async (schema) => {
    try {
      const response = await schemasAPI.toggleStatus(schema._id);
      toast.success(response.data.message || 'Schema status updated successfully');
      fetchSchemas();
      handleMenuClose();
    } catch (error) {
      console.error('Error toggling schema status:', error);
      toast.error('Failed to update schema status');
    }
  };

  const handleMenuClick = (event, schema) => {
    setMenuAnchor(event.currentTarget);
    setSelectedSchema(schema);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedSchema(null);
  };

  const handleViewRecords = (schema) => {
    setRecordsSchema(schema);
    setRecordsDialogOpen(true);
    handleMenuClose();
  };

  const handleCopyApiEndpoint = (schema) => {
    const endpoint = `${window.location.origin}/api/data/${schema.slug}`;
    navigator.clipboard.writeText(endpoint);
    toast.success('API endpoint copied to clipboard');
    handleMenuClose();
  };

  const handleShowApiInfo = (schema) => {
    setApiInfoSchema(schema);
    setApiInfoDialogOpen(true);
    handleMenuClose();
  };

  const handleShowWebsiteData = () => {
    // Group schemas by website and collect all websites that have schemas
    const websiteGroups = {};
    schemas.forEach(schema => {
      if (!websiteGroups[schema.website._id]) {
        websiteGroups[schema.website._id] = {
          website: schema.website,
          schemas: []
        };
      }
      websiteGroups[schema.website._id].schemas.push(schema);
    });

    // Get all websites that have schemas
    const websitesWithData = Object.values(websiteGroups).map(group => group.website);

    if (websitesWithData.length > 0) {
      setWebsitesWithSchemas(websitesWithData);
      setWebsiteDataDialogOpen(true);
    }
  };

  const handlePublishWebsite = async (website) => {
    if (!website.buildHookUrl) {
      toast.error('No build hook URL configured for this website');
      return;
    }

    try {
      setPublishingWebsite(website._id);
      const response = await websitesAPI.publishWebsite(website._id);

      if (response.data.success) {
        toast.success('Website published successfully! Build triggered.');
      } else {
        toast.error(response.data.message || 'Failed to publish website');
      }
    } catch (error) {
      console.error('Error publishing website:', error);

      if (error.response?.status === 429) {
        toast.error('Daily publish limit reached. Try again tomorrow.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Invalid request');
      } else {
        toast.error('Failed to publish website');
      }
    } finally {
      setPublishingWebsite(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <ActiveIcon />;
      case 'draft': return <EditIcon />;
      case 'archived': return <ArchiveIcon />;
      default: return <SchemaIcon />;
    }
  };

  const fieldTypes = [
    'string', 'number', 'boolean', 'date', 'email', 'url', 'text', 'array', 'object', 'dropdown', 'object_array'
  ];

  // Utility functions for JSON import
  const detectFieldType = (value, key) => {
    if (value === null || value === undefined) return 'string';

    // Check for email pattern
    if (typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'email';
    }

    // Check for URL pattern
    if (typeof value === 'string' && /^https?:\/\/.+/.test(value)) {
      return 'url';
    }

    // Check for date pattern (ISO string or common date formats)
    if (typeof value === 'string' && (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value) ||
      /^\d{4}-\d{2}-\d{2}$/.test(value) ||
      /^\d{2}\/\d{2}\/\d{4}$/.test(value)
    )) {
      return 'date';
    }

    // Check for long text (more than 100 characters)
    if (typeof value === 'string' && value.length > 100) {
      return 'text';
    }

    // Basic type detection
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string') return 'string';
    if (Array.isArray(value)) {
      // Check if it's an array of objects
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0])) {
        return 'object_array';
      }
      return 'array';
    }
    if (typeof value === 'object') return 'object';

    return 'string';
  };

  const generateArraySchema = (arrayValue) => {
    if (!Array.isArray(arrayValue) || arrayValue.length === 0) return [];

    // Take the first object as a sample to generate schema
    const sampleObject = arrayValue[0];
    if (typeof sampleObject !== 'object' || sampleObject === null || Array.isArray(sampleObject)) {
      return [];
    }

    const schema = [];
    Object.entries(sampleObject).forEach(([key, value]) => {
      const type = detectFieldType(value, key);
      schema.push({
        name: key,
        label: key, // Use exact field name as label
        type: type === 'object_array' ? 'string' : type, // Nested object arrays not supported in array schema
        required: false, // Keep required off by default for all fields
        description: ``,
        options: []
      });
    });

    return schema;
  };

  const parseJsonToFields = (jsonString) => {
    try {
      const jsonData = JSON.parse(jsonString);
      if (typeof jsonData !== 'object' || jsonData === null || Array.isArray(jsonData)) {
        throw new Error('JSON must be an object, not an array or primitive value');
      }

      const fields = [];
      const errors = [];

      Object.entries(jsonData).forEach(([key, value]) => {
        // Validate field name
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
          errors.push(`Invalid field name "${key}". Field names must start with a letter or underscore and contain only letters, numbers, and underscores.`);
          return;
        }

        if (key === 'id') {
          errors.push(`Field name "id" is reserved and will be automatically added to all schemas.`);
          return;
        }

        const type = detectFieldType(value, key);
        const field = {
          name: key,
          label: key, // Use exact field name as label
          type,
          required: false, // Keep required off by default for all fields
          description: ``,
          options: [],
          arraySchema: []
        };

        // If it's an object_array, generate the array schema
        if (type === 'object_array' && Array.isArray(value)) {
          field.arraySchema = generateArraySchema(value);
        }

        fields.push(field);
      });

      return { fields, errors };
    } catch (error) {
      return {
        fields: [],
        errors: [`Invalid JSON: ${error.message}`]
      };
    }
  };

  const handleJsonImport = () => {
    if (!jsonInput.trim()) {
      setJsonErrors(['Please enter JSON data']);
      return;
    }

    const { fields, errors } = parseJsonToFields(jsonInput);
    setDetectedFields(fields);
    setJsonErrors(errors);
  };

  const handleApplyJsonFields = () => {
    if (detectedFields.length === 0) {
      toast.error('No fields detected from JSON');
      return;
    }

    // Add all detected fields
    detectedFields.forEach(field => {
      append(field);
    });

    // Close the import dialog
    setJsonImportOpen(false);
    setJsonInput('');
    setDetectedFields([]);
    setJsonErrors([]);

    toast.success(`Successfully imported ${detectedFields.length} fields from JSON`);
  };

  const handleApplySingleField = (field) => {
    // Check if field name already exists
    const currentFields = watch('fields') || [];
    const fieldExists = currentFields.some(f => f.name === field.name);

    if (fieldExists) {
      toast.error(`Field "${field.name}" already exists in the schema`);
      return;
    }

    // Add the single field
    append(field);

    // Remove the field from detected fields
    setDetectedFields(prev => prev.filter(f => f.name !== field.name));

    toast.success(`Added field "${field.name}" to schema`);
  };

  const handleCloseJsonImport = () => {
    setJsonImportOpen(false);
    setJsonInput('');
    setDetectedFields([]);
    setJsonErrors([]);
  };

  const formatRecordValue = (value, fieldType) => {
    if (value === null || value === undefined) return '-';

    switch (fieldType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'array':
        if (Array.isArray(value)) {
          // For display, show first few items with count if many
          if (value.length <= 2) {
            return value.join(', ');
          } else {
            return `${value.slice(0, 2).join(', ')} +${value.length - 2} more`;
          }
        }
        return JSON.stringify(value);
      case 'object':
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
      case 'object_array':
        if (Array.isArray(value)) {
          if (value.length === 0) return 'Empty array';
          if (value.length === 1) return `1 item`;
          return `${value.length} items`;
        }
        return JSON.stringify(value);
      default:
        return String(value);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Tabs and Buttons */}
      <Box sx={{ mb: 3 }}>
        {/* Mobile-responsive Tabs with Add Record Button */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-start' },
            overflow: 'auto'
          }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                label={`Data (${totalRecords})`}
                icon={<DataIcon />}
                iconPosition="start"
                sx={{ minWidth: { xs: 120, sm: 'auto' } }}
              />
              <Tab
                label={`Schemas (${schemas.length})`}
                icon={<SchemaIcon />}
                iconPosition="start"
                sx={{ minWidth: { xs: 120, sm: 'auto' } }}
              />
            </Tabs>
          </Box>

          {/* Add Record Button - only show when Data tab is selected */}
          {tabValue === 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenRecordDialog()}
                disabled={schemas.length === 0}
                size="small"
                fullWidth={{ xs: true, sm: false }}
                sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
              >
                Add Record
              </Button>
            </Box>
          )}
        </Box>

        {/* Action Buttons Below Tabs - only show on Schema tab */}
        {tabValue === 1 && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'flex-end',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 2
          }}>
            {schemas.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<DataIcon />}
                onClick={handleShowWebsiteData}
                color="secondary"
                size="small"
                fullWidth={{ xs: true, sm: false }}
              >
                Website Data
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
              fullWidth={{ xs: true, sm: false }}
            >
              New Schema
            </Button>
          </Box>
        )}
      </Box>

      {/* Data Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Data Filters */}
          <Box sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 200 },
                maxWidth: { xs: '100%', sm: 300 }
              }}
            >
              <InputLabel>Filter by Schema</InputLabel>
              <Select
                value={selectedSchemaFilter}
                onChange={handleSchemaFilterChange}
                label="Filter by Schema"
                startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                <MenuItem value="all">All Schemas</MenuItem>
                {schemas.map((schema) => (
                  <MenuItem key={schema._id} value={schema._id}>
                    {schema.name} ({schema.statistics?.totalRecords || 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Data Records Table */}
          {dataLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          ) : dataRecords.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <DataIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No data records found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  {schemas.length === 0
                    ? 'Create a schema first, then add data records'
                    : 'Add your first data record to get started'
                  }
                </Typography>
                {schemas.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenRecordDialog()}
                  >
                    Add Record
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Paper>
              {/* Mobile Row View for small screens */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Data</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }} align="right">Actions</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Schema</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataRecords.map((record) => (
                        <TableRow key={record._id} hover>
                          <TableCell sx={{ py: 1, px: 1 }}>
                            <Box sx={{ maxWidth: 120 }}>
                              {record.schema.fields.filter(field => field.name !== 'id').slice(0, 1).map((field) => (
                                <Typography
                                  key={field.name}
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.7rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                  }}
                                >
                                  <strong>{field.label}:</strong> {formatRecordValue(record.data[field.name], field.type)}
                                </Typography>
                              ))}
                              {record.schema.fields.filter(field => field.name !== 'id').length > 1 && (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                  +{record.schema.fields.filter(field => field.name !== 'id').length - 1} more
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 1 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {new Date(record.createdAt).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                {new Date(record.updatedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1, px: 1 }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenRecordDialog(record)}
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRecord(record._id)}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1, px: 1 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {record.schema.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                {record.schema.slug}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Desktop Table View for larger screens */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                        <TableCell>Schema</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataRecords.map((record) => (
                        <TableRow key={record._id} hover>
                          <TableCell>
                            <Box sx={{ maxWidth: 300 }}>
                              {record.schema.fields.filter(field => field.name !== 'id').slice(0, 3).map((field) => (
                                <Typography
                                  key={field.name}
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <strong>{field.label}:</strong> {formatRecordValue(record.data[field.name], field.type)}
                                </Typography>
                              ))}
                              {record.schema.fields.filter(field => field.name !== 'id').length > 3 && (
                                <Typography variant="caption" color="textSecondary">
                                  +{record.schema.fields.filter(field => field.name !== 'id').length - 3} more fields
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(record.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(record.createdAt).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(record.updatedAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(record.updatedAt).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenRecordDialog(record)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRecord(record._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">
                                {record.schema.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {record.schema.slug}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Schema Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Schemas Grid */}
          {schemas.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <SchemaIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No schemas found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Create your first data schema to start managing custom data for your websites
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Create Schema
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {schemas.map((schema) => (
                <Grid item xs={12} sm={6} lg={4} key={schema._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-4px)' },
                        boxShadow: { xs: 2, sm: 4 }
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h2" gutterBottom>
                            {schema.name}
                          </Typography>
                          <Chip
                            label={schema.status}
                            color={getStatusColor(schema.status)}
                            size="small"
                            icon={getStatusIcon(schema.status)}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, schema)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>

                      {/* Schema Info */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Slug: <strong>{schema.slug}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Website: <strong>{schema.website?.name}</strong>
                        </Typography>
                        {schema.description && (
                          <Typography variant="body2" sx={{ mt: 1 }} noWrap>
                            {schema.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Statistics */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" color="primary">
                            {schema.fields?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Fields
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" color="secondary">
                            {schema.statistics?.totalRecords || 0}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Records
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6">
                            v{schema.version}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Version
                          </Typography>
                        </Box>
                      </Box>

                      {/* API Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {schema.isPublic ? (
                          <Chip icon={<PublicIcon />} label="Public API" size="small" color="success" />
                        ) : (
                          <Chip icon={<PrivateIcon />} label="Private" size="small" />
                        )}
                      </Box>
                    </CardContent>

                    <CardActions sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0.5 },
                      p: { xs: 2, sm: 1 }
                    }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(schema)}
                        fullWidth={{ xs: true, sm: false }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DataIcon />}
                        onClick={() => handleViewRecords(schema)}
                        color="primary"
                        fullWidth={{ xs: true, sm: false }}
                      >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          Data ({schema.statistics?.totalRecords || 0})
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          Records ({schema.statistics?.totalRecords || 0})
                        </Box>
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopyApiEndpoint(schema)}
                        color="secondary"
                        fullWidth={{ xs: true, sm: false }}
                      >
                        API
                      </Button>
                      {schema.website?.buildHookUrl && (
                        <Button
                          size="small"
                          startIcon={publishingWebsite === schema.website._id ? <CircularProgress size={16} /> : <PublishIcon />}
                          onClick={() => handlePublishWebsite(schema.website)}
                          color="success"
                          disabled={publishingWebsite === schema.website._id}
                          fullWidth={{ xs: true, sm: false }}
                        >
                          Publish
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleOpenDialog(selectedSchema)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Schema</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleViewRecords(selectedSchema)}>
          <ListItemIcon>
            <DataIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Data</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleCopyApiEndpoint(selectedSchema)}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy API Endpoint</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleShowApiInfo(selectedSchema)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>API Documentation</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedSchema)}>
          <ListItemIcon>
            <PowerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedSchema?.status === 'active' ? 'Archive' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleDeleteClick(selectedSchema)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Schema</ListItemText>
        </MenuItem>
      </Menu>

      {/* Schema Dialog - Will continue in next part due to length */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={{ xs: true, sm: false }}
        PaperProps={{
          sx: {
            minHeight: { xs: '100vh', sm: '70vh' },
            m: { xs: 0, sm: 3 }
          }
        }}
      >
        <form onSubmit={handleSubmit(handleSubmitSchema)}>
          <DialogTitle>
            {editingSchema ? 'Edit Schema' : 'Create New Schema'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Schema name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Schema Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="slug"
                  control={control}
                  rules={{
                    required: 'Schema slug is required',
                    pattern: {
                      value: /^[a-z0-9-_]+$/,
                      message: 'Slug can only contain lowercase letters, numbers, hyphens, and underscores'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Schema Slug"
                      fullWidth
                      error={!!errors.slug}
                      helperText={errors.slug?.message || 'Used in API endpoints (auto-generated from name)'}
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={2}
                      helperText="Optional description for this schema"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="websiteId"
                  control={control}
                  rules={{ required: 'Website is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.websiteId} size="small">
                      <InputLabel>Website</InputLabel>
                      <Select {...field} label="Website">
                        {websites.map((website) => (
                          <MenuItem key={website._id} value={website._id}>
                            {website.name} ({website.siteId})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Fields Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Schema Fields
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CodeIcon />}
                      onClick={() => setJsonImportOpen(true)}
                      color="secondary"
                      size="small"
                    >
                      Import from JSON
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => append({ name: '', label: '', type: 'string', required: false, description: '', options: [], arraySchema: [] })}
                    >
                      Add Field
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {fields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">
                        {field.name === 'id' ? 'ID Field (System Generated)' : `Field ${index + 1}`}
                      </Typography>
                      {fields.length > 1 && field.name !== 'id' && (
                        <IconButton
                          size="small"
                          onClick={() => remove(index)}
                          color="error"
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name={`fields.${index}.name`}
                          control={control}
                          rules={{
                            required: 'Field name is required',
                            pattern: {
                              value: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                              message: 'Field name must start with letter or underscore'
                            },
                            validate: (value) => {
                              if (value === 'id' && field.name !== 'id') {
                                return 'The "id" field is automatically added to all schemas';
                              }
                              return true;
                            }
                          }}
                          render={({ field: controllerField }) => (
                            <TextField
                              {...controllerField}
                              label="Field Name"
                              fullWidth
                              size="small"
                              disabled={field.name === 'id'}
                              error={!!errors.fields?.[index]?.name}
                              helperText={errors.fields?.[index]?.name?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Controller
                          name={`fields.${index}.label`}
                          control={control}
                          rules={{ required: 'Field label is required' }}
                          render={({ field: controllerField }) => (
                            <TextField
                              {...controllerField}
                              label="Field Label"
                              fullWidth
                              size="small"
                              disabled={field.name === 'id'}
                              error={!!errors.fields?.[index]?.label}
                              helperText={errors.fields?.[index]?.label?.message}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Controller
                          name={`fields.${index}.type`}
                          control={control}
                          render={({ field: controllerField }) => (
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                {...controllerField}
                                label="Type"
                                disabled={field.name === 'id'}
                              >
                                {fieldTypes.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {type}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.description`}
                          control={control}
                          render={({ field: controllerField }) => (
                            <TextField
                              {...controllerField}
                              label="Description (Optional)"
                              fullWidth
                              size="small"
                              multiline
                              rows={1}
                              disabled={field.name === 'id'}
                            />
                          )}
                        />
                      </Grid>

                      {/* Dropdown Options - only show for dropdown fields */}
                      {watch(`fields.${index}.type`) === 'dropdown' && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Dropdown Options
                          </Typography>
                          <Controller
                            name={`fields.${index}.options`}
                            control={control}
                            render={({ field: optionsField }) => (
                              <DropdownOptionsEditor
                                options={optionsField.value || []}
                                onChange={optionsField.onChange}
                                disabled={field.name === 'id'}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {/* Object Array Schema Editor - only show for object_array fields */}
                      {watch(`fields.${index}.type`) === 'object_array' && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom>
                            Object Array Schema
                          </Typography>
                          <Controller
                            name={`fields.${index}.arraySchema`}
                            control={control}
                            render={({ field: objectArrayField }) => (
                              <ObjectArraySchemaEditor
                                arraySchema={objectArrayField.value || []}
                                onChange={objectArrayField.onChange}
                                disabled={field.name === 'id'}
                              />
                            )}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Controller
                          name={`fields.${index}.required`}
                          control={control}
                          render={({ field: controllerField }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={controllerField.value}
                                  onChange={controllerField.onChange}
                                  disabled={field.name === 'id'}
                                />
                              }
                              label="Required Field"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}

              {/* Settings Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Schema Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="settings.maxRecords"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Max Records"
                      type="number"
                      fullWidth
                      inputProps={{ min: 1, max: 10000 }}
                      helperText="Maximum number of records allowed"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="settings.enableValidation"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          }
                          label="Enable Validation"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Controller
                      name="settings.enableVersioning"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          }
                          label="Enable Versioning"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Controller
                      name="settings.enableIndexing"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          }
                          label="Enable Indexing"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            p: { xs: 2, sm: 1 }
          }}>
            <Button
              onClick={handleCloseDialog}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              fullWidth={{ xs: true, sm: false }}
            >
              {isSubmitting ? 'Saving...' : (editingSchema ? 'Update Schema' : 'Create Schema')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Schema</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the schema "{schemaToDelete?.name}"?
            This action cannot be undone.
          </Typography>
          {schemaToDelete?.statistics?.totalRecords > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This schema contains {schemaToDelete.statistics.totalRecords} data records.
              You cannot delete a schema with existing data. Consider archiving it instead.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={schemaToDelete?.statistics?.totalRecords > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schema Records Dialog */}
      {recordsDialogOpen && (
        <SchemaRecordsDialog
          open={recordsDialogOpen}
          onClose={() => setRecordsDialogOpen(false)}
          schema={recordsSchema}
          onRecordsUpdate={fetchSchemas}
        />
      )}

      {/* API Documentation Dialog */}
      {apiInfoDialogOpen && (
        <ApiEndpointInfo
          open={apiInfoDialogOpen}
          onClose={() => setApiInfoDialogOpen(false)}
          schema={apiInfoSchema}
        />
      )}

      {/* Data Record Dialog */}
      {recordDialogOpen && (
        <DataRecordDialog
          open={recordDialogOpen}
          onClose={handleCloseRecordDialog}
          schema={selectedRecordSchema}
          schemas={schemas}
          record={editingRecord}
          onRecordSaved={handleRecordSaved}
        />
      )}

      {/* Website Data Dialog */}
      {websiteDataDialogOpen && (
        <WebsiteDataDialog
          open={websiteDataDialogOpen}
          onClose={() => setWebsiteDataDialogOpen(false)}
          websites={websitesWithSchemas}
        />
      )}

      {/* JSON Import Dialog */}
      <Dialog
        open={jsonImportOpen}
        onClose={handleCloseJsonImport}
        maxWidth="md"
        fullWidth
        fullScreen={{ xs: true, sm: false }}
        PaperProps={{
          sx: {
            minHeight: { xs: '100vh', sm: '70vh' },
            m: { xs: 0, sm: 3 }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          Import Schema Fields from JSON
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* JSON Input Section */}
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Paste your JSON object below. The system will automatically detect field types and generate schema fields.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Supported auto-detection:</strong> string, number, boolean, email, URL, date, text (long strings),
                  arrays, objects, and object arrays with nested schemas.
                </Typography>
              </Alert>
              <TextField
                multiline
                rows={10}
                fullWidth
                placeholder={`{
  "productName": "Sample Product",
  "price": 29.99,
  "inStock": true,
  "releaseDate": "2024-01-15",
  "description": "This is a long description that will be detected as text type...",
  "contactEmail": "contact@example.com",
  "website": "https://example.com",
  "tags": ["electronics", "gadgets"],
  "specifications": {
    "weight": "1.2kg",
    "dimensions": "10x5x2cm"
  },
  "reviews": [
    {
      "rating": 5,
      "comment": "Great product!",
      "reviewer": "John Doe"
    }
  ]
}`}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                helperText="Enter a valid JSON object (not an array or primitive value)"
                sx={{ fontFamily: 'monospace' }}
              />
            </Grid>

            {/* JSON Analysis Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleJsonImport}
                  disabled={!jsonInput.trim()}
                >
                  Analyze JSON
                </Button>
                <Typography variant="body2" color="textSecondary">
                  Click to detect fields from your JSON data
                </Typography>
              </Box>
            </Grid>

            {/* Errors Display */}
            {jsonErrors.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="subtitle2" gutterBottom>
                    Issues found:
                  </Typography>
                  <List dense>
                    {jsonErrors.map((error, index) => (
                      <ListItem key={index} disablePadding>
                        <MuiListItemText
                          primary={error}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Grid>
            )}

            {/* Detected Fields Preview */}
            {detectedFields.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchemaIcon color="primary" />
                  Detected Fields ({detectedFields.length})
                </Typography>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Found {detectedFields.length} field{detectedFields.length !== 1 ? 's' : ''} in your JSON.
                  You can add individual fields using the "Add" buttons, or add all at once with "Add All Fields" below.
                </Alert>

                {/* Color Legend */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                    Field Type Colors:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: 0.5 }} />
                      <Typography variant="caption">Array</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#f3e5f5', border: '1px solid #9c27b0', borderRadius: 0.5 }} />
                      <Typography variant="caption">Object Array</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: '#fff3e0', border: '1px solid #ff9800', borderRadius: 0.5 }} />
                      <Typography variant="caption">Object</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'transparent', border: '1px solid #ccc', borderRadius: 0.5 }} />
                      <Typography variant="caption">Other Types</Typography>
                    </Box>
                  </Box>
                </Box>

                <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detectedFields.map((field, index) => {
                        // Define background colors for different field types
                        const getRowBackgroundColor = (fieldType) => {
                          switch (fieldType) {
                            case 'array':
                              return '#e3f2fd'; // Light blue for arrays
                            case 'object_array':
                              return '#f3e5f5'; // Light purple for object arrays
                            case 'object':
                              return '#fff3e0'; // Light orange for objects
                            default:
                              return 'transparent';
                          }
                        };

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              backgroundColor: getRowBackgroundColor(field.type),
                              '&:hover': {
                                backgroundColor: field.type === 'array' ? '#bbdefb' :
                                  field.type === 'object_array' ? '#e1bee7' :
                                    field.type === 'object' ? '#ffcc02' :
                                      'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {field.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {field.label}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={field.type}
                                size="small"
                                color={
                                  field.type === 'object_array' ? 'secondary' :
                                    field.type === 'array' ? 'info' :
                                      field.type === 'object' ? 'warning' :
                                        'primary'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={field.required ? 'Yes' : 'No'}
                                size="small"
                                color={field.required ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {field.type === 'object_array' && field.arraySchema?.length > 0 && (
                                <Tooltip title={`Object schema: ${field.arraySchema.map(s => s.name).join(', ')}`}>
                                  <Chip
                                    label={`${field.arraySchema.length} properties`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                              {field.type === 'array' && (
                                <Chip
                                  label="Simple array"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                              {field.type === 'object' && (
                                <Chip
                                  label="JSON object"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleApplySingleField(field)}
                                sx={{ minWidth: 'auto', px: 1 }}
                              >
                                Add
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button
            onClick={handleCloseJsonImport}
            fullWidth={{ xs: true, sm: false }}
          >
            {detectedFields.length === 0 ? 'Close' : 'Cancel'}
          </Button>
          {detectedFields.length > 0 && (
            <>
              <Button
                variant="outlined"
                onClick={handleApplyJsonFields}
                startIcon={<AddIcon />}
                fullWidth={{ xs: true, sm: false }}
                sx={{ mr: { xs: 0, sm: 1 } }}
              >
                Add All {detectedFields.length} Field{detectedFields.length !== 1 ? 's' : ''}
              </Button>
              <Typography variant="body2" color="textSecondary" sx={{
                display: { xs: 'block', sm: 'flex' },
                alignItems: 'center',
                textAlign: { xs: 'center', sm: 'left' },
                mt: { xs: 1, sm: 0 },
                fontSize: '0.75rem'
              }}>
                Or use "Add" buttons above for individual fields
              </Typography>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchemasPage; 